/**
 * <vimeo-lightbox-video>
 *
 * Companion to sections/hero-video-lightbox.liquid. Plays a Vimeo video
 * (including unlisted videos via the privacy hash) inside the theme's
 * <modal-dialog> lightbox using the official Player API — the same machinery
 * video-autoplay-sound.js uses for the inline advent-calendar player.
 *
 * Unlike the pre-baked lazyload iframe pattern, nothing is fetched from
 * Vimeo until the visitor shows intent: the Player API script is warmed on
 * hover/focus of the play button, and the player itself is built on first
 * open (autoplaying with sound — the click counts as user activation; if the
 * browser still blocks audible autoplay we fall back to muted playback with
 * visible controls). Closing the modal pauses; reopening resumes.
 *
 * Required markup (inside a <modal-dialog id="VideoLightbox-...">):
 *   <vimeo-lightbox-video data-video-id="123" [data-video-hash="abc"]>
 *     <div data-vimeo-target></div>
 *   </vimeo-lightbox-video>
 */
if (!customElements.get('vimeo-lightbox-video')) {
  const VIMEO_API_SRC = 'https://player.vimeo.com/api/player.js';

  const loadVimeoApi = () => {
    if (window.__vimeoApiPromise) return window.__vimeoApiPromise;

    window.__vimeoApiPromise = new Promise((resolve, reject) => {
      if (window.Vimeo && window.Vimeo.Player) {
        resolve();
        return;
      }
      const existing = document.querySelector('script[src="' + VIMEO_API_SRC + '"]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', reject);
        return;
      }
      const script = document.createElement('script');
      script.src = VIMEO_API_SRC;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return window.__vimeoApiPromise;
  };

  class VimeoLightboxVideo extends HTMLElement {
    connectedCallback() {
      // ModalDialog re-parents itself to <body> on connect, which re-fires
      // connectedCallback — bind everything exactly once.
      if (this.bound) return;
      this.bound = true;

      this.modal = this.closest('modal-dialog');
      this.target = this.querySelector('[data-vimeo-target]');
      this.videoId = parseInt(this.dataset.videoId, 10);
      this.videoHash = this.dataset.videoHash || null;
      this.player = null;

      if (!this.modal || !this.target || !this.videoId) return;

      new MutationObserver(() => {
        if (this.modal.hasAttribute('open')) {
          this.onOpen();
        } else {
          this.onClose();
        }
      }).observe(this.modal, { attributes: true, attributeFilter: ['open'] });

      // Warm the Player API on intent so click → play isn't gated on the
      // network fetch (keeps audible autoplay inside the activation window).
      const toggle = document.getElementById(this.modal.id.replace('VideoLightbox-', 'VideoLightboxToggle-'));
      if (toggle) {
        ['pointerenter', 'focus'].forEach((eventName) => {
          toggle.addEventListener(eventName, loadVimeoApi, { once: true });
        });
      }
    }

    onOpen() {
      // ModalDialog.show() never moves focus in; without this, ESC-to-close
      // only works after the visitor clicks inside the dialog.
      const dialog = this.modal.querySelector('[role="dialog"]');
      if (dialog) dialog.focus();

      if (this.player) {
        this.player.play().catch(() => {});
        return;
      }

      loadVimeoApi()
        .then(() => this.createPlayer())
        .catch(() => {});
    }

    onClose() {
      if (this.player) this.player.pause().catch(() => {});
    }

    createPlayer() {
      const options = {
        autoplay: true,
        muted: false,
        loop: false,
        controls: true,
        playsinline: true,
        responsive: false,
        dnt: true,
        title: false,
        byline: false,
        portrait: false
      };
      if (this.videoHash) {
        options.url = 'https://vimeo.com/' + this.videoId + '/' + this.videoHash;
      } else {
        options.id = this.videoId;
      }

      try {
        this.player = new window.Vimeo.Player(this.target, options);
      } catch (e) {
        return;
      }

      this.player.ready()
        .then(() => this.player.setMuted(false))
        .then(() => this.player.play())
        .catch(() => {
          // Audible autoplay blocked — degrade to muted playback with controls.
          this.player.setMuted(true)
            .then(() => this.player.play())
            .catch(() => {});
        });
    }
  }

  customElements.define('vimeo-lightbox-video', VimeoLightboxVideo);
}
